import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Target, MessageSquare, Palette, Mail, Globe, Camera, ArrowRight, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.png';
import apparelCrewneck from '@/assets/apparel/crewneck.jpg';
import apparelHoodie from '@/assets/apparel/hoodie.jpg';
import apparelJoggers from '@/assets/apparel/joggers.jpg';
import apparelTank from '@/assets/apparel/tank.jpg';
import apparelCap from '@/assets/apparel/cap.jpg';
import apparelGymBag from '@/assets/apparel/gym-bag.png';

const shopProducts = [
  { title: 'The Signature Essential Crewneck', image: apparelCrewneck },
  { title: 'The Studio Essential Hoodie', image: apparelHoodie },
  { title: 'The Signature Athletic Jogger', image: apparelJoggers },
  { title: 'The Performance Tank', image: apparelTank },
  { title: 'The Range Cap', image: apparelCap },
  { title: 'Gym Bag', image: apparelGymBag },
];

const services = [
  { icon: Target, label: 'Paid Advertising', desc: 'Meta, Google & TikTok ads', href: '/services/paid-advertising' },
  { icon: Mail, label: 'Email & SMS Marketing', desc: 'Automated sequences', href: '/services/email-marketing' },
  { icon: MessageSquare, label: 'Social Media', desc: 'Organic growth strategy', href: '/services/social-media' },
  { icon: Globe, label: 'Web Design & CRO', desc: 'High-converting sites', href: '/services/web-design' },
  { icon: Palette, label: 'Branding & Apparel', desc: 'Brand identity & merch', href: '/services/branding-apparel' },
  { icon: Camera, label: 'Content Creation', desc: 'Video & graphic assets', href: '/services/content-creation' },
  
];

const Navbar = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const shopDropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const shopTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsServicesOpen(false), 200);
  };

  const handleShopMouseEnter = () => {
    clearTimeout(shopTimeoutRef.current);
    setIsShopOpen(true);
  };

  const handleShopMouseLeave = () => {
    shopTimeoutRef.current = setTimeout(() => setIsShopOpen(false), 200);
  };

  const navLinks = [
    { label: 'Process', href: isHomePage ? '#process' : '/#process' },
    { label: 'About', href: isHomePage ? '#about' : '/#about' },
    { label: 'Contact', href: isHomePage ? '#contact' : '/#contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-4 left-4 right-4 z-50 transition-all duration-500 rounded-2xl ${
          isScrolled
            ? 'bg-card/60 backdrop-blur-xl border border-border/50 shadow-lg py-3'
            : 'bg-card/30 backdrop-blur-md border border-border/30 py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={logo} alt="Archer Marketing" width={40} height={40} decoding="async" fetchPriority="high" className="w-10 h-10 transition-transform duration-300 group-hover:scale-110" />
              <span className="text-xl font-bold text-foreground">
                Archer<span className="text-primary">.</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/services/strategy-consulting" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative group">
                Strategy
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>

              {/* Services Dropdown */}
              <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <button className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative group flex items-center gap-1">
                  Services
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </button>

                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[520px] rounded-xl bg-card border border-border shadow-2xl z-[60] overflow-hidden"
                    >
                      <div className="grid grid-cols-2 gap-1 p-3">
                        {services.map((service) => (
                          <Link
                            key={service.label}
                            to={service.href}
                            onClick={() => setIsServicesOpen(false)}
                            className="flex items-start gap-3 rounded-lg p-3 hover:bg-muted/50 transition-colors duration-200 group/item"
                          >
                            <service.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-foreground group-hover/item:text-primary transition-colors">{service.label}</p>
                              <p className="text-xs text-muted-foreground">{service.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="border-t border-border px-3 py-2.5">
                        <a
                          href={isHomePage ? '#services' : '/#services'}
                          onClick={() => setIsServicesOpen(false)}
                          className="flex items-center justify-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        >
                          View All Services <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.slice(0, 2).map((link) => (
                <a key={link.label} href={link.href} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative group">
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </a>
              ))}

              {/* Shop Dropdown */}
              <div ref={shopDropdownRef} className="relative" onMouseEnter={handleShopMouseEnter} onMouseLeave={handleShopMouseLeave}>
                <a
                  href="https://archer-apparel.printify.me/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative group flex items-center gap-1"
                >
                  Shop
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isShopOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </a>

                <AnimatePresence>
                  {isShopOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-4 w-[340px] rounded-xl bg-card border border-border shadow-2xl z-[60] overflow-hidden"
                    >
                      <div className="p-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 pb-2">Latest Drops</p>
                        <div className="grid grid-cols-2 gap-2">
                          {shopProducts.map((product) => (
                            <a
                              key={product.title}
                              href="https://archer-apparel.printify.me/"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setIsShopOpen(false)}
                              className="group/item rounded-lg overflow-hidden hover:bg-muted/50 transition-colors duration-200"
                            >
                              <div className="aspect-square overflow-hidden rounded-lg">
                                <img
                                  src={product.image}
                                  alt={product.title}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover/item:scale-105"
                                />
                              </div>
                              <p className="text-xs font-medium text-foreground px-1 py-1.5 leading-tight group-hover/item:text-primary transition-colors">{product.title}</p>
                            </a>
                          ))}
                        </div>
                      </div>
                      <div className="border-t border-border px-3 py-2.5">
                        <a
                          href="https://archer-apparel.printify.me/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsShopOpen(false)}
                          className="flex items-center justify-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        >
                          Visit Store <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.slice(2).map((link) => (
                <a key={link.label} href={link.href} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative group">
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a href="https://www.cal.eu/archer-marketing" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="lg">Book a Discovery Call</Button>
              </a>
            </div>

            <div className="flex md:hidden items-center gap-2">
              <button className="text-foreground p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}>
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 bg-background/95 backdrop-blur-xl transition-all duration-500 md:hidden ${isMobileMenuOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'}`}>
        <div className="flex flex-col items-center justify-center h-full gap-6">
          <Link to="/services/strategy-consulting" onClick={() => setIsMobileMenuOpen(false)} className="text-2xl font-bold text-foreground hover:text-primary transition-colors duration-300">
            Strategy
          </Link>

          {/* Mobile Services Expandable */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
              className="text-2xl font-bold text-foreground hover:text-primary transition-colors duration-300 flex items-center gap-2"
            >
              Services
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isMobileServicesOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-col items-center gap-3 pt-4">
                    {services.map((service) => (
                      <Link
                        key={service.label}
                        to={service.href}
                        onClick={() => { setIsMobileMenuOpen(false); setIsMobileServicesOpen(false); }}
                        className="text-base text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2"
                      >
                        <service.icon className="w-4 h-4 text-primary" />
                        {service.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map((link) => (
            <a key={link.label} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-2xl font-bold text-foreground hover:text-primary transition-colors duration-300">
              {link.label}
            </a>
          ))}
          <a href="https://archer-apparel.printify.me/" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="text-2xl font-bold text-foreground hover:text-primary transition-colors duration-300">
            Shop
          </a>
          <a href="https://www.cal.eu/archer-marketing" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
            <Button variant="hero" size="xl">Book a Discovery Call</Button>
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
